'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { CameraOutlined } from '@ant-design/icons';
import { FaCamera } from 'react-icons/fa';
import { fetchCommunityImages } from '../utils/reviewerApi';
import type { CommunityImage } from '../types';

// ── Props ─────────────────────────────────────────────────────

interface CommunityPhotosProps {
  entityId: number;
  entityName: string;
  onUploadClick?: () => void;
}

// ── Component ─────────────────────────────────────────────────

/**
 * Acquisition-only surface for community photos.
 *
 * Approved community photos are already server-merged into the hero
 * gallery (see `buildHeroGallery` on the campsite page, credited with a
 * "📷 by First L." pill), so this component does NOT re-render them — a
 * dedicated strip would just show the same images a second time.
 *
 * Its sole job is to give photo-less camps (the common case, incl. every
 * free camp) a visible "be the first to add a photo" upload entry point.
 * Once the first photo is approved it flows into the hero and this CTA
 * disappears.
 */
const CommunityPhotos: React.FC<CommunityPhotosProps> = ({
  entityId,
  entityName,
  onUploadClick,
}) => {
  const [images, setImages] = useState<CommunityImage[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Fetch images ─────────────────────────────────────────────

  const loadImages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCommunityImages(entityId);
      const list: CommunityImage[] = data?.images ?? data ?? [];
      setImages(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[CommunityPhotos] Failed to fetch images:', err);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // ── Render ───────────────────────────────────────────────────

  // Nothing to show while loading, when photos already exist (the hero
  // covers them), or when there's no upload handler wired in.
  if (loading || images.length > 0 || !onUploadClick) {
    return null;
  }

  return (
    <div className="ca-community-empty bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-3">
        <FaCamera className="text-gray-500" size={16} />
        <h3 className="text-lg font-semibold text-gray-900">Community Photos</h3>
      </div>
      <button
        onClick={onUploadClick}
        className="w-full flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:border-accent hover:bg-[#e3f2fd] transition-colors py-8"
      >
        <CameraOutlined className="text-2xl text-gray-400" />
        <span className="text-sm font-semibold text-gray-700">Be the first to add a photo of {entityName}</span>
        <span className="text-xs text-gray-400">Share your snap to help fellow campers</span>
      </button>
    </div>
  );
};

export default CommunityPhotos;
